import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';

// eslint-disable-next-line no-undef
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

export default function CookieConsent() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);

  // 1. LAZY INIT: Kontrollime localStorage-it KOHE muutuja loomisel.
  // See lahendab "cascading renders" vea, sest me ei muuda olekut hiljem useEffectis.
  const [isInitialized, setIsInitialized] = useState(() => {
    return localStorage.getItem('cookie_consent') === 'granted';
  });

  // 2. See Effect tegeleb AINULT GA käivitamisega, kui 'isInitialized' muutub tõeseks
  useEffect(() => {
    if (isInitialized) {
      if (GA_MEASUREMENT_ID && typeof GA_MEASUREMENT_ID === 'string') {
        // Kontrollime, et poleks juba initsialiseeritud (ReactGA sisemine kontroll)
        if (!window.ga_initialized) { 
           ReactGA.initialize(GA_MEASUREMENT_ID);
           window.ga_initialized = true; // Väike häkk, et vältida topelt-init dev mode'is
        }
      } else {
        console.warn('Google Analytics ID puudub!');
      }
    }
  }, [isInitialized]);

  // 3. Pageview jälgimine (töötab ainult siis, kui on initsialiseeritud)
  useEffect(() => {
    if (isInitialized) {
      ReactGA.send({ hitType: "pageview", page: location.pathname + location.search });
    }
  }, [location, isInitialized]);

  // 4. Bänneri näitamine (ainult siis, kui valikut pole tehtud)
  useEffect(() => {
    if (localStorage.getItem('cookie_consent') === null) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie_consent', 'granted');
    setIsInitialized(true); // See käivitab ülemise useEffecti, mis teeb ReactGA.initialize()
    setIsVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookie_consent', 'denied');
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-up">
      <div className="max-w-4xl mx-auto bg-slate-900/95 backdrop-blur-md border border-slate-700 text-white p-6 rounded-2xl shadow-2xl flex flex-col md:flex-row items-center gap-6 justify-between">
        <div className="text-sm text-slate-300 md:max-w-lg">
          <p>
            {t('cookie.text')}{' '}
            <Link to="/privacy" className="text-blue-400 hover:text-white underline decoration-blue-400/30 underline-offset-4 transition-colors">
              {t('cookie.privacy_link')}
            </Link>.
          </p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button onClick={handleDecline} className="flex-1 md:flex-none px-4 py-2.5 rounded-xl border border-slate-600 text-slate-300 font-bold text-sm hover:bg-slate-800 hover:text-white transition-all whitespace-nowrap">
            {t('cookie.decline')}
          </button>
          <button onClick={handleAccept} className="flex-1 md:flex-none px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-500 shadow-lg shadow-blue-900/20 transition-all transform hover:-translate-y-0.5 whitespace-nowrap">
            {t('cookie.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}
